/**
 * My Fleet — client-side store for a shopper's saved machines (Year · Machine · Model).
 *
 * Single source of truth for every Fleet surface (header chip + drawer, PDP fit
 * badge, context bar, /pages/fleet). State is held in memory, persisted to
 * localStorage, and — when the shopper is signed in and account sync is enabled —
 * reconciled with the customer's server copy through the `/apps/search/fleet`
 * App Proxy endpoint.
 *
 * Surfaces stay in sync by listening for the `fleet:change` CustomEvent on
 * `document` (or via `Fleet.subscribe`). The module is a singleton: every import
 * of `@theme/fleet` shares the same in-memory state.
 *
 * Local-first by design: all reads/writes hit localStorage synchronously and
 * never block on the network. Sync is best-effort and degrades silently when the
 * endpoint is unavailable, so the feature works for guests and offline.
 */

const STORAGE_KEY = 'fleet';
const SYNC_META_KEY = 'fleet:sync';
const SCHEMA_VERSION = 1;
const DEFAULT_MAX = 8;
/** Absolute ceiling regardless of the merchant setting — bounds storage + PUT size. */
const HARD_MAX = 50;
const SYNC_DEBOUNCE_MS = 800;
const SYNC_ENDPOINT = '/apps/search/fleet';

/** Event dispatched on `document` whenever fleet state changes. */
export const FLEET_CHANGE = 'fleet:change';

/* ------------------------------------------------------------------ config -- */

/** Read the Liquid-seeded config off the global `Theme` object, defensively. */
function cfg() {
  try {
    return (typeof Theme !== 'undefined' && Theme && Theme.fleet) || {};
  } catch (_) {
    return {};
  }
}

function maxMachines() {
  const n = Number(cfg().maxMachines);
  return Number.isFinite(n) && n > 0 ? Math.min(n, HARD_MAX) : DEFAULT_MAX;
}

function customerId() {
  const id = cfg().customerId;
  return id ? String(id) : '';
}

function syncEnabled() {
  return Boolean(cfg().syncEnabled) && customerId() !== '';
}

/* ------------------------------------------------------- normalize / shape -- */

const norm = (s) => String(s == null ? '' : s).trim();
const lc = (s) => norm(s).toLowerCase();

/**
 * Stable, case/whitespace-insensitive dedupe key for a machine.
 * @param {{year?: string, machine?: string, model?: string}} m
 * @returns {string}
 */
export function machineId(m) {
  return [lc(m && m.year), lc(m && m.machine), lc(m && m.model)].join('|');
}

function labelFor(m) {
  return [norm(m.year), norm(m.machine), norm(m.model)].filter(Boolean).join(' · ');
}

/**
 * Build the fitment-search deep link for a machine (mirrors buildResultsUrl in
 * blocks/ymm-cascade.liquid). Empty parts are omitted.
 * @param {{year?: string, machine?: string, model?: string}} m
 * @returns {string}
 */
export function shopUrl(m) {
  const p = new URLSearchParams();
  if (m && norm(m.year)) p.set('year', norm(m.year));
  if (m && norm(m.machine)) p.set('machine', norm(m.machine));
  if (m && norm(m.model)) p.set('model', norm(m.model));
  return `/pages/fitment-search?${p.toString()}`;
}

const nowMs = () => Date.now();

function emptyDoc() {
  return { version: SCHEMA_VERSION, machines: [], activeId: null, updatedAt: 0 };
}

/** Coerce an unknown object into a Machine, or null if it lacks year+machine. */
function sanitizeMachine(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const year = norm(raw.year);
  const machine = norm(raw.machine);
  const model = norm(raw.model);
  if (!year || !machine) return null; // model is optional
  const m = { year, machine, model };
  return {
    id: machineId(m),
    year,
    machine,
    model,
    label: labelFor(m),
    addedAt: Number(raw.addedAt) || nowMs(),
  };
}

/** Normalize a whole doc: dedupe, sort most-recent-first, clamp, fix activeId. */
function sanitizeDoc(doc) {
  const out = emptyDoc();
  const seen = new Set();
  const machines = Array.isArray(doc && doc.machines) ? doc.machines : [];
  for (const raw of machines) {
    const m = sanitizeMachine(raw);
    if (!m || seen.has(m.id)) continue;
    seen.add(m.id);
    out.machines.push(m);
  }
  out.machines.sort((a, b) => b.addedAt - a.addedAt);
  // Resolve activeId BEFORE clamping so clampMachines() can protect the active
  // machine from being trimmed, then re-validate against the clamped list.
  const activeId = doc && doc.activeId != null ? String(doc.activeId) : null;
  out.activeId = activeId && out.machines.some((m) => m.id === activeId) ? activeId : firstId(out.machines);
  clampMachines(out);
  if (!out.activeId || !out.machines.some((m) => m.id === out.activeId)) {
    out.activeId = firstId(out.machines);
  }
  out.updatedAt = Number(doc && doc.updatedAt) || 0;
  return out;
}

function firstId(machines) {
  return machines[0] ? machines[0].id : null;
}

/** Trim to the configured cap while guaranteeing the active machine survives. */
function clampMachines(doc) {
  const max = maxMachines();
  if (doc.machines.length <= max) return;
  let kept = doc.machines.slice(0, max);
  if (doc.activeId && !kept.some((m) => m.id === doc.activeId)) {
    const active = doc.machines.find((m) => m.id === doc.activeId);
    if (active) kept = kept.slice(0, max - 1).concat(active);
  }
  doc.machines = kept;
}

/* ----------------------------------------------------------- persistence --- */

function safeGet(key) {
  try {
    return localStorage.getItem(key);
  } catch (_) {
    return null;
  }
}

function safeSet(key, value) {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (_) {
    return false; // private mode / quota — operate in-memory for this session
  }
}

/* ----------------------------------------------------------- module state -- */

/** @type {{version:number, machines:Array, activeId:string|null, updatedAt:number}|null} */
let state = null;
/** @type {{lastServerUpdatedAt:number, dirty:boolean, lastCustomerId:string}|null} */
let syncMeta = null;
let loaded = false;
let readyPromise = null;
let syncAvailable = true; // flipped false after a failed probe, retried next load
let inFlight = false;
let syncTimer = null;
let forceDirty = false;
let flushBound = false;
const subscribers = new Set();

function loadFromStorage() {
  let doc = emptyDoc();
  const raw = safeGet(STORAGE_KEY);
  if (raw) {
    try {
      doc = JSON.parse(raw);
    } catch (_) {
      doc = emptyDoc();
    }
  }
  state = sanitizeDoc(migrate(doc));

  let meta = { lastServerUpdatedAt: 0, dirty: false, lastCustomerId: '' };
  const rawMeta = safeGet(SYNC_META_KEY);
  if (rawMeta) {
    try {
      meta = Object.assign(meta, JSON.parse(rawMeta) || {});
    } catch (_) {
      /* keep defaults */
    }
  }
  syncMeta = meta;
}

/** Forward-migrate older docs. Today only v1 exists; reshape anything else. */
function migrate(doc) {
  if (!doc || typeof doc !== 'object') return emptyDoc();
  if (Number(doc.version) === SCHEMA_VERSION) return doc;
  return {
    version: SCHEMA_VERSION,
    machines: Array.isArray(doc.machines) ? doc.machines : [],
    activeId: doc.activeId != null ? doc.activeId : null,
    updatedAt: Number(doc.updatedAt) || 0,
  };
}

function ensureLoaded() {
  if (loaded) return;
  loadFromStorage();
  loaded = true;
  try {
    window.addEventListener('storage', (e) => {
      if (e.key !== STORAGE_KEY) return;
      loadFromStorage();
      emit('hydrate');
    });
  } catch (_) {
    /* no window storage events available */
  }
}

/* -------------------------------------------------------------- emit / subs */

function snapshot() {
  return { machines: state.machines.map((m) => ({ ...m })), activeId: state.activeId };
}

function emit(reason) {
  const detail = { ...snapshot(), reason };
  for (const fn of subscribers) {
    try {
      fn(detail);
    } catch (e) {
      console.error(e);
    }
  }
  try {
    document.dispatchEvent(new CustomEvent(FLEET_CHANGE, { detail }));
  } catch (_) {
    /* document unavailable */
  }
}

/** Persist + mark dirty + schedule a sync + notify. Call after every mutation. */
function commit(reason) {
  state.updatedAt = nowMs();
  safeSet(STORAGE_KEY, JSON.stringify(state));
  syncMeta.dirty = true;
  persistMeta();
  emit(reason);
  scheduleSync();
}

function persistMeta() {
  safeSet(SYNC_META_KEY, JSON.stringify(syncMeta));
}

/**
 * Keep a fleet surface (header chip + drawer list, context bar, PDP fit badge,
 * /pages/fleet) rendered from the store even when Horizon re-renders/morphs its
 * section after first paint — which reverts our JS-applied DOM back to the
 * server HTML and would otherwise leave the surface blank until the next store
 * mutation (the "shows for a beat, then goes empty on refresh" bug).
 *
 * Renders immediately, on every `fleet:change`, and again whenever an external
 * re-render mutates the host (self-heal). The MutationObserver is disconnected
 * around our own render so it can never feed back into itself.
 *
 * @param {Element} el - the custom-element host
 * @param {() => void} render
 * @returns {{ stop: () => void }}
 */
export function selfHeal(el, render) {
  let mo = null;
  const OPTS = { attributes: true, childList: true, subtree: true, characterData: true };
  const safe = () => {
    if (mo) mo.disconnect();
    try {
      render();
    } finally {
      if (mo && el.isConnected) mo.observe(el, OPTS);
    }
  };
  safe(); // initial render (observer not wired yet)
  document.addEventListener(FLEET_CHANGE, safe);
  mo = new MutationObserver(safe);
  mo.observe(el, OPTS);
  return {
    stop() {
      document.removeEventListener(FLEET_CHANGE, safe);
      if (mo) mo.disconnect();
      mo = null;
    },
  };
}

/* ------------------------------------------------------------- public API -- */

function list() {
  ensureLoaded();
  return state.machines.map((m) => ({ ...m }));
}

function getActiveId() {
  ensureLoaded();
  return state.activeId;
}

function getActive() {
  ensureLoaded();
  if (!state.activeId) return null;
  const m = state.machines.find((x) => x.id === state.activeId);
  return m ? { ...m } : null;
}

/**
 * Add (or promote) a machine. Returns its id, or null if year/machine missing.
 * @param {{year:string, machine:string, model?:string}} input
 * @returns {string|null}
 */
function add(input) {
  ensureLoaded();
  const m = sanitizeMachine(input);
  if (!m) return null;

  const idx = state.machines.findIndex((x) => x.id === m.id);
  if (idx >= 0) {
    const existing = state.machines.splice(idx, 1)[0];
    existing.addedAt = nowMs();
    existing.label = m.label;
    state.machines.unshift(existing);
    state.activeId = existing.id;
    commit('add');
    return existing.id;
  }

  m.addedAt = nowMs();
  state.machines.unshift(m);
  state.activeId = m.id;
  clampMachines(state);
  commit('add');
  return m.id;
}

function remove(id) {
  ensureLoaded();
  id = String(id);
  const idx = state.machines.findIndex((m) => m.id === id);
  if (idx < 0) return false;
  state.machines.splice(idx, 1);
  if (state.activeId === id) state.activeId = firstId(state.machines);
  commit('remove');
  return true;
}

function setActive(id) {
  ensureLoaded();
  if (id == null) {
    if (state.activeId === null) return true;
    state.activeId = null;
    commit('active');
    return true;
  }
  id = String(id);
  if (!state.machines.some((m) => m.id === id)) return false;
  if (state.activeId === id) return true;
  state.activeId = id;
  commit('active');
  return true;
}

function has(input) {
  ensureLoaded();
  let id;
  if (typeof input === 'string') id = input;
  else if (input && typeof input === 'object') id = machineId(input);
  else return false;
  return state.machines.some((m) => m.id === id);
}

function clear() {
  ensureLoaded();
  if (state.machines.length === 0 && state.activeId === null) return;
  state.machines = [];
  state.activeId = null;
  commit('clear');
}

function count() {
  ensureLoaded();
  return state.machines.length;
}

function max() {
  return maxMachines();
}

function isFull() {
  ensureLoaded();
  return state.machines.length >= maxMachines();
}

function subscribe(fn) {
  ensureLoaded();
  subscribers.add(fn);
  try {
    fn({ ...snapshot(), reason: 'init' });
  } catch (e) {
    console.error(e);
  }
  return () => subscribers.delete(fn);
}

/**
 * Load local state and, when signed in + sync enabled, reconcile with the
 * server. Idempotent — returns the same promise on repeat calls.
 * @returns {Promise<void>}
 */
function ready() {
  if (readyPromise) return readyPromise;
  ensureLoaded();
  readyPromise = (async () => {
    detectLogin();
    if (syncEnabled()) await reconcile();
    bindFlushHandlers();
  })();
  return readyPromise;
}

async function refreshFromServer() {
  ensureLoaded();
  if (!syncEnabled()) return;
  syncAvailable = true;
  await reconcile();
}

/* ----------------------------------------------------------------- syncing */

/** Detect login/logout transitions across page loads via the seeded customerId. */
function detectLogin() {
  const cid = customerId();
  if (cid && syncMeta.lastCustomerId !== cid) {
    // Fresh login (or a different customer) on this browser. If a guest fleet was
    // built locally, force a dirty reconcile so it merges up into the account.
    if (state.machines.length > 0) forceDirty = true;
    syncMeta.lastCustomerId = cid;
    persistMeta();
  } else if (!cid && syncMeta.lastCustomerId) {
    // Logged out: keep the local copy, stop syncing, clear server bookkeeping.
    syncMeta.lastCustomerId = '';
    syncMeta.lastServerUpdatedAt = 0;
    syncMeta.dirty = false;
    persistMeta();
  }
}

async function reconcile() {
  if (!syncEnabled() || !syncAvailable) return;

  let serverDoc;
  try {
    const resp = await fetch(SYNC_ENDPOINT, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!resp.ok) {
      syncAvailable = false; // endpoint not live / unauthorized — go local-only this page
      return;
    }
    const data = await resp.json();
    serverDoc = data && data.doc ? sanitizeDoc(migrate(data.doc)) : emptyDoc();
  } catch (_) {
    syncAvailable = false;
    return;
  }

  const merged = mergeDocs(state, serverDoc);
  const localChanged = sigOf(merged) !== sigOf(state);
  const serverChanged = sigOf(merged) !== sigOf(serverDoc);

  state = merged;
  if (localChanged) {
    safeSet(STORAGE_KEY, JSON.stringify(state));
    emit('sync');
  }

  syncMeta.lastServerUpdatedAt = serverDoc.updatedAt || 0;
  if (serverChanged || forceDirty || syncMeta.dirty) {
    forceDirty = false;
    syncMeta.dirty = true;
    persistMeta();
    await pushNow();
  } else {
    syncMeta.dirty = false;
    persistMeta();
  }
}

/** Union machines by id (newest addedAt wins); LWW on activeId by updatedAt. */
function mergeDocs(localDoc, serverDoc) {
  const byId = new Map();
  for (const m of serverDoc.machines) byId.set(m.id, m);
  for (const m of localDoc.machines) {
    const ex = byId.get(m.id);
    if (!ex || m.addedAt >= ex.addedAt) byId.set(m.id, m);
  }
  const merged = {
    version: SCHEMA_VERSION,
    machines: Array.from(byId.values()).sort((a, b) => b.addedAt - a.addedAt),
    activeId: (localDoc.updatedAt || 0) >= (serverDoc.updatedAt || 0) ? localDoc.activeId : serverDoc.activeId,
    updatedAt: Math.max(localDoc.updatedAt || 0, serverDoc.updatedAt || 0),
  };
  clampMachines(merged);
  if (!merged.activeId || !merged.machines.some((m) => m.id === merged.activeId)) {
    merged.activeId = firstId(merged.machines);
  }
  return merged;
}

/** Identity signature for change detection (ignores updatedAt churn). */
function sigOf(doc) {
  return JSON.stringify({ m: doc.machines.map((x) => `${x.id}@${x.addedAt}`), a: doc.activeId });
}

function scheduleSync() {
  if (!syncEnabled()) return;
  if (syncTimer) clearTimeout(syncTimer);
  syncTimer = setTimeout(() => {
    syncTimer = null;
    pushNow();
  }, SYNC_DEBOUNCE_MS);
}

async function pushNow(opts) {
  opts = opts || {};
  if (!syncEnabled() || !syncAvailable) return;
  if (inFlight) return; // a mutation mid-flight stays dirty; next schedule retries
  if (!syncMeta.dirty && !opts.force) return;

  inFlight = true;
  const sentSig = sigOf(state);
  const body = JSON.stringify({ doc: state });
  try {
    const resp = await fetch(SYNC_ENDPOINT, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body,
      keepalive: Boolean(opts.keepalive),
    });
    if (!resp.ok) {
      // 5xx may be transient (stay available + dirty to retry); 4xx means go quiet.
      if (resp.status < 500) syncAvailable = false;
      return;
    }
    const data = await resp.json();
    if (sigOf(state) !== sentSig) {
      // A mutation landed during the in-flight PUT — it was not in this request.
      // Keep dirty and reschedule so the newer state is pushed; don't adopt the
      // server's now-stale updatedAt.
      persistMeta();
      scheduleSync();
      return;
    }
    if (data && data.doc) {
      const serverDoc = sanitizeDoc(migrate(data.doc));
      // Adopt the server's monotonic updatedAt; our machines are echoed back.
      state.updatedAt = serverDoc.updatedAt || state.updatedAt;
      syncMeta.lastServerUpdatedAt = state.updatedAt;
      safeSet(STORAGE_KEY, JSON.stringify(state));
    }
    syncMeta.dirty = false;
    persistMeta();
  } catch (_) {
    // Leave dirty; a later load/mutation retries.
  } finally {
    inFlight = false;
  }
}

function bindFlushHandlers() {
  if (flushBound) return;
  flushBound = true;
  const flush = () => {
    if (syncEnabled() && syncMeta && syncMeta.dirty) pushNow({ keepalive: true, force: true });
  };
  try {
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flush();
    });
  } catch (_) {
    /* no-op */
  }
}

/* ----------------------------------------------------------------- exports */

export const Fleet = {
  list,
  getActive,
  getActiveId,
  add,
  remove,
  setActive,
  has,
  clear,
  count,
  max,
  isFull,
  ready,
  refreshFromServer,
  subscribe,
};
